import React, { useState } from 'react';
import {
    Box,
    Typography,
    Container,
    Paper,
    Divider,
    Switch,
    FormControlLabel,
    Button,
    Alert
} from '@mui/material';
import { config_strings }  from './config/config';

const CookieSettings = () => {
    const { legal } = config_strings;
    const { cookie_settings, company_info } = legal;

    // Initialize cookie preferences from localStorage or defaults
    const [cookiePreferences, setCookiePreferences] = useState(() => {
        const saved = localStorage.getItem('cookiePreferences');
        return saved ? JSON.parse(saved) : {
            essential: true,
            analytics: false,
            marketing: false
        };
    });

    const [showSavedMessage, setShowSavedMessage] = useState(false);

    const handlePreferenceChange = (category) => (event) => {
        setCookiePreferences(prev => ({
            ...prev,
            [category]: event.target.checked
        }));
    };

    const handleSavePreferences = () => {
        localStorage.setItem('cookiePreferences', JSON.stringify(cookiePreferences));
        setShowSavedMessage(true);
        setTimeout(() => setShowSavedMessage(false), 3000);
        
        // Here you would typically trigger your analytics/marketing scripts
        // based on the user's preferences
        console.log('Cookie preferences saved:', cookiePreferences);
    };

    const handleAcceptAll = () => {
        const allAccepted = {
            essential: true,
            analytics: true,
            marketing: true
        };
        setCookiePreferences(allAccepted);
        localStorage.setItem('cookiePreferences', JSON.stringify(allAccepted));
        setShowSavedMessage(true);
        setTimeout(() => setShowSavedMessage(false), 3000);
    };

    const handleRejectAll = () => {
        const essentialOnly = {
            essential: true,
            analytics: false,
            marketing: false
        };
        setCookiePreferences(essentialOnly);
        localStorage.setItem('cookiePreferences', JSON.stringify(essentialOnly));
        setShowSavedMessage(true);
        setTimeout(() => setShowSavedMessage(false), 3000);
    };

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Paper elevation={1} sx={{ p: 4 }}>
                <Typography variant="h4" gutterBottom>
                    {cookie_settings.title}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" paragraph>
                    {cookie_settings.description}
                </Typography>

                <Divider sx={{ my: 3 }} />

                {showSavedMessage && (
                    <Alert severity="success" sx={{ mb: 3 }}>
                        Your cookie preferences have been saved successfully.
                    </Alert>
                )}

                <Box sx={{ '& > *': { mb: 3 } }}>
                    {Object.entries(cookie_settings.categories).map(([key, category]) => (
                        <Box key={key} sx={{ 
                            p: 2, 
                            border: 1, 
                            borderColor: 'divider', 
                            borderRadius: 1,
                            mb: 2
                        }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h6" gutterBottom>
                                        {category.title}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {category.description}
                                    </Typography>
                                </Box>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={cookiePreferences[key]}
                                            onChange={handlePreferenceChange(key)}
                                            disabled={category.required}
                                        />
                                    }
                                    label={category.required ? "Required" : "Optional"}
                                    sx={{ ml: 2 }}
                                />
                            </Box>
                        </Box>
                    ))}
                </Box>

                <Divider sx={{ my: 3 }} />

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button 
                        variant="contained" 
                        onClick={handleSavePreferences}
                        sx={{ minWidth: 120 }}
                    >
                        Save Preferences
                    </Button>
                    <Button 
                        variant="outlined" 
                        onClick={handleAcceptAll}
                        sx={{ minWidth: 120 }}
                    >
                        Accept All
                    </Button>
                    <Button 
                        variant="outlined" 
                        onClick={handleRejectAll}
                        sx={{ minWidth: 120 }}
                    >
                        Reject All
                    </Button>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                        <strong>{company_info.name}</strong><br />
                        For more information, see our Privacy Policy or contact us at {company_info.contact_email}
                    </Typography>
                </Box>
            </Paper>
        </Container>
    );
};

export default CookieSettings;